<template>
    <div class="secure-sign__container">
        <v-container>
            <s-s-form-header
                title="Denzapine Monitoring Service Website User Registration Form"
                document-number="PV01F07"
                version="9.0"
                effective-date="29 Feb 2024"
                review-date="29 Feb 2026"
                supersedes="8.0"
            ></s-s-form-header>
            <v-col class="text-center">
                <h1 class="mb-5">Denzapine Monitoring Service Website User Registration Form</h1>
                <h4 class="mb-8">
                    Please complete all form fields before submission of application. Missing information may result in
                    delays to registration.
                </h4>
            </v-col>
            <v-col cols="12">
                <v-form ref="form" v-model="isFormValid">
                    <v-row>
                        <v-col cols="12" sm="4" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::websiteUserRegistrationForm.jobPosition"
                                label=""
                                mandatory
                                :rules="validationRules.jobPosition"
                                :radio-data="[
                                    { label: 'Pharmacist / Technician', value: 'Pharmacist / Technician' },
                                    { label: 'Dr. / Nurse', value: 'Dr. / Nurse' },
                                ]"
                                :disabled="isDisabled"
                            ></secure-sign-form-radio-group>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">User Information</h5>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::websiteUserRegistrationForm.userFirstName"
                                :rules="validationRules.userFirstName"
                                label="First Name of New User:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                                :disabled="isDisabled"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::websiteUserRegistrationForm.userLastName"
                                :rules="validationRules.userLastName"
                                label="Last Name of New User:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                                :disabled="isDisabled"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::websiteUserRegistrationForm.dispensingPharmacy"
                                :rules="validationRules.dispensingPharmacy"
                                label="Dispensing Pharmacy/Pharmacies or Pharmacy Group Name:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                                :disabled="isDisabled && !isForApproval"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Contact Information</h5>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::websiteUserRegistrationForm.address"
                                :rules="validationRules.address"
                                label="Address:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                                :disabled="isDisabled"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::websiteUserRegistrationForm.telephoneNumber"
                                :rules="validationRules.telephoneNumber"
                                label="Telephone Number:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                                :disabled="isDisabled"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="6" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::websiteUserRegistrationForm.emailAddress"
                                :rules="validationRules.emailAddress"
                                label="Email Address:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                                :disabled="isDisabled"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">User Access level (Select one)</h5>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::websiteUserRegistrationForm.userAccessLevel"
                                label=""
                                mandatory
                                :rules="validationRules.userAccessLevel"
                                :disabled="isDisabled && !isForApproval"
                                :radio-data="[
                                    {
                                        label: 'Healthcare Professional Limited',
                                        value: 'Healthcare Professional Limited',
                                        info: [
                                            'Read only access to all patients assigned to their pharmacy site/group',
                                        ],
                                    },
                                    {
                                        label: 'Healthcare Professional-pharmacy-dispensers',
                                        value: 'Healthcare Professional-pharmacy-dispensers',
                                        info: [
                                            'Users will be able to add blood results to patients assigned to their pharmacy site/group',
                                            'Users can add dispensing rights against individual blood results for patients assigned to their pharmacy site/group. Users will not be able to cancel or amend a patient’s treatment schedule, including adding a treatment break and stopping the dispensing on the patient’s profile',
                                            '* Users can update consultants, pharmacy sites and collection centre information for patients assigned to their pharmacy site/group',
                                        ],
                                    },
                                    {
                                        label: 'Healthcare Professional',
                                        value: 'Healthcare Professional',
                                        info: [
                                            'Users will be able to add blood results to patients assigned to their pharmacy site/group',
                                            '* Users can update consultants, pharmacy sites and collection centre information for patients assigned to their pharmacy site/group',
                                        ],
                                    },
                                    {
                                        label: 'HCP Trained on Analyzer',
                                        value: 'HCP Trained on Analyzer',
                                        info: [
                                            'Users have access to NPT/blood analysing machines',
                                            'Users will be able to add blood results to patients assigned to their pharmacy site/group',
                                            '* Users can update consultants, pharmacy sites and collection centre information for patients assigned to their pharmacy site/group',
                                        ],
                                    },
                                    {
                                        label: 'HCP Analyser and Dispenser',
                                        value: 'HCP Analyser and Dispenser',
                                        info: [
                                            'Users have access to NPT/blood analysing machines',
                                            'Users will be able to add blood results to patients assigned to their pharmacy site/group',
                                            '* Users can update consultants, pharmacy sites and collection centre information for patients assigned to their pharmacy site/group',
                                            'Users can add dispensing rights against individual blood results for patients assigned to their pharmacy site/group. Users will not be able to cancel or amend a patient’s treatment schedule, including adding a treatment break and stopping the dispensing on the patient’s profile',
                                        ],
                                    },
                                ]"
                            ></secure-sign-form-radio-group>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <p>
                                *All these requests will be saved and sent as requests via the Denzapine Monitoring
                                Service website notification functionality. They will be reviewed by the Britannia
                                Denzapine Monitoring Service Team and actioned accordingly.
                            </p>
                        </v-col>
                    </v-row>
                    <v-row>
                        <h3>In signing this document, you agree to abide by the conditions outlined below:</h3>
                        <v-col cols="12">
                            <ul>
                                <li>
                                    You will not use any of the information contained on the DMS for any purpose other
                                    than to monitor the suitability of patients for treatment with Denzapine.
                                </li>
                                <li>
                                    If you are granted Read/Write access, you will be responsible for the accuracy of
                                    information that you add to DMS.
                                </li>
                                <li>
                                    When access is no longer required you will inform the Denzapine Monitoring Team and
                                    your user ID will be disabled.
                                </li>
                                <li>
                                    Your user ID may only be used by you, the registered user, and you will not give
                                    your user ID or password to anyone else.
                                </li>
                                <li>
                                    You will be responsible for any malicious software, viruses or other destructive
                                    computer programs downloaded from your computer/server onto DMS and you will ensure
                                    your computer systems have suitable and up-to-date anti-virus and security software.
                                </li>
                                <li>
                                    Britannia may disable your user ID if you fail to comply with, or if Britannia has
                                    reasonable grounds to believe you have failed to comply with any of the above
                                    conditions. Britannia reserves the right to change these conditions upon reasonable
                                    notice. Your user ID registration confers no ownership rights over DMS which remains
                                    the sole property of Britannia Pharmaceuticals Ltd. Your personal data will only be
                                    used by us in accordance with Britannia’s Privacy Policy which can be found at
                                    <a href="https://www.britannia-pharm.co.uk/privacy-policy/" target="_blank"
                                        >https://www.britannia-pharm.co.uk/privacy-policy/</a
                                    >
                                </li>
                            </ul>
                        </v-col>
                    </v-row>

                    <v-row :class="!isDisabled ? 'border-approver-details' : ''">
                        <v-col cols="12">
                            <h5 v-if="!isDisabled" class="patient-form-title mt-5">Please enter approver details</h5>
                            <h5 v-else class="patient-form-title mb-5">Approver details</h5>
                            <p v-if="!isDisabled" class="mt-2 text-grey">
                                Approvers have to be a Pharmacist or Consultant who are registered in the DMS. They have
                                to be different <br />
                                from the person requesting access to the DMS.
                            </p>
                        </v-col>
                        <v-row>
                            <v-col cols="12" sm="8" class="py-0">
                                <b-textarea
                                    to-mutate="secure_sign_forms::websiteUserRegistrationForm.approverName"
                                    :rules="validationRules.approverName"
                                    label="Approver Name:"
                                    mandatory
                                    :rows="1"
                                    :auto-grow="true"
                                    :printable="true"
                                    :disabled="isDisabled"
                                ></b-textarea>
                                <v-row>
                                    <v-col cols="6">
                                        <b-textarea
                                            to-mutate="secure_sign_forms::websiteUserRegistrationForm.approverEmail"
                                            label="Email Address:"
                                            :rules="validationRules.approverEmail"
                                            mandatory
                                            :rows="1"
                                            :auto-grow="true"
                                            :printable="true"
                                            :disabled="isDisabled"
                                        ></b-textarea>
                                    </v-col>
                                    <v-col cols="6">
                                        <b-textarea
                                            to-mutate="secure_sign_forms::websiteUserRegistrationForm.approverPhoneNumber"
                                            label="Phone Number:"
                                            :rules="validationRules.approverPhoneNumber"
                                            mandatory
                                            :rows="1"
                                            :auto-grow="true"
                                            :printable="true"
                                            :disabled="isDisabled"
                                        ></b-textarea>
                                    </v-col>
                                    <v-col v-if="isForApproval">
                                        <ul>
                                            <li>
                                                In approving this form, you are giving your consent for the requester to have access to the DMS for all patients associated with the above pharmacy(s). You recognise that this may give access to more than just their own direct patient caseload. Please note that the Denzapine Monitoring Services reserves the right to review any request for read/write access.
                                            </li>
                                        </ul>
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-col cols="12" sm="4" class="py-0">
                                <b-date-picker-store
                                    hide-details
                                    to-mutate="secure_sign_forms::websiteUserRegistrationForm.date"
                                    label="Date"
                                    :rules="validationRules.date"
                                    mandatory
                                    :disabled="isDisabled"
                                ></b-date-picker-store>
                            </v-col>
                        </v-row>
                    </v-row>

                    <v-row>
                        <v-col v-if="!isDisabled" cols="12" sm="8">
                            <h5 class="patient-form-title mt-5 mb-2">Signature of the requester:</h5>
                            <secure-sign-form-signature-component
                                ref="signaturePad"
                                :form-type="form"
                                :is-disabled="isDisabled"
                                :disabled="isDisabled"
                            ></secure-sign-form-signature-component>
                        </v-col>
                        <v-col
                            v-if="!isDisabled"
                            cols="12"
                            sm="4"
                            class="d-flex flex-column justify-content-end align-items-center"
                        >
                            <secure-sign-form-recaptcha
                                ref="recaptcha"
                                :valid-recaptcha="validRecaptcha"
                                :is-disabled="isDisabled"
                                @expired="expired"
                                @verify="onRecaptchaVerify"
                            ></secure-sign-form-recaptcha>
                            <v-btn
                                :loading="loading"
                                class="mt-2"
                                max-width="304px"
                                width="100%"
                                size="x-large"
                                color="primary"
                                :disabled="isDisabled"
                                @click="submit"
                                >Submit
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col
                            v-if="isDisabled && !isExpired"
                            cols="12"
                            class="d-flex flex-column justify-content-start align-items-stretch mt-5"
                        >
                            <v-row class="text-h6">Approver Comments:</v-row>
                            <v-col class="custom-approver-block">
                                <b-textarea
                                    class="approver-comment-field"
                                    label=""
                                    to-mutate="secure_sign_forms::websiteUserRegistrationForm.approverData.comment"
                                    :rules="validationRules.comment"
                                    :rows="3"
                                    :auto-grow="true"
                                    :printable="true"
                                    :required="isRejected"
                                    :mandatory="isRejected"
                                    :disabled="isProcessed"
                                ></b-textarea>
                            </v-col>
                        </v-col>
                        <v-col v-if="isDisabled" cols="12" sm="8">
                            <v-row class="text-h6">Approver Signature:</v-row>
                            <v-col class="custom-approver-block">
                                <secure-sign-form-signature-component
                                    ref="approverSignaturePad"
                                    :form-type="form"
                                    signature-field="approverData.approverSignature"
                                    :is-disabled="!isForApproval || isProcessed || isExpired"
                                    :disabled="!isForApproval || isProcessed || isExpired"
                                ></secure-sign-form-signature-component>
                            </v-col>
                        </v-col>
                        <v-col cols="12" class="d-flex align-items-center">
                            <p v-if="isProcessed" class="mx-3 red--text text-weight-bold text-h6">
                                * The form has been processed and cannot be edited again!
                            </p>
                            <p v-if="isExpired" class="mx-3 red--text text-weight-bold text-h6">
                                The 14-day form validity has passed.
                            </p>
                        </v-col>
                        <v-col v-if="!isExpired" cols="12" class="d-flex justify-content-end align-items-center">
                            <b-confirm
                                v-if="isDisabled && isForApproval && !isProcessed"
                                :text="t('default::ssf.confirm.text.approve')"
                                :title="t('default::ssf.confirm.title.approve')"
                                :width="600"
                                @true="processRegistrationForms('approve')"
                            >
                                <template #button="{ on }">
                                    <v-btn color="primary" rounded class="previewBtn mx-3" v-on="on">
                                        {{ t('default::ssf.btn.approve') }}
                                    </v-btn>
                                </template>
                            </b-confirm>

                            <b-confirm
                                v-if="isDisabled && isForApproval && !isProcessed"
                                :text="t('default::ssf.confirm.text.reject')"
                                :title="t('default::ssf.confirm.title.reject')"
                                :width="600"
                                @true="processRegistrationForms('reject')"
                            >
                                <template #button="{ on }">
                                    <v-btn
                                        color="error"
                                        rounded
                                        class="previewBtn mx-3"
                                        @click.stop="validateComment($event, on)"
                                    >
                                        {{ t('default::ssf.btn.reject') }}
                                    </v-btn>
                                </template>
                            </b-confirm>
                        </v-col>
                    </v-row>
                    <s-s-form-footer ref="footer">
                        <p>
                            Should you have any queries, please do not hesitate to contact the Denzapine Monitoring
                            Service Team on 0333 200 4141
                        </p>
                        <p>Job Code: UK-Denz-2300034, Version 3.0, Date of Prep: Jul 2026.</p>
                    </s-s-form-footer>
                </v-form>
            </v-col>
        </v-container>
    </div>
</template>

<script>
import SSFormHeader from './partials/SSFormHeader.vue';
import SSFormFooter from './partials/SSFormFooter.vue';

let formControl = require('./mixins/FormControl.js').default;
export default {
    components: { SSFormFooter, SSFormHeader },
    mixins: [formControl],
    data() {
        return {
            validationRules: {
                jobPosition: [(v) => !!v || t('users::validation.field.required')],
                userFirstName: [(v) => !!v || t('users::validation.field.required')],
                userLastName: [(v) => !!v || t('users::validation.field.required')],
                dispensingPharmacy: [(v) => !!v || t('users::validation.field.required')],
                address: [(v) => !!v || t('users::validation.field.required')],
                telephoneNumber: [(v) => !!v || t('users::validation.field.required')],
                emailAddress: [(v) => !!v || t('users::validation.field.required'), this.$root.rules.email],
                userAccessLevel: [(v) => !!v || t('users::validation.field.required')],
                approverName: [(v) => !!v || t('users::validation.field.required')],
                date: [(v) => !!v || t('users::validation.field.required')],
                approverEmail: [(v) => !!v || t('users::validation.field.required'), this.$root.rules.email],
                approverPhoneNumber: [(v) => !!v || t('users::validation.field.required')],
                comment: [(v) => !this.isRejected || !!v || t('users::validation.field.required')],
            },
            isFormValid: false,
            formType: 'website-user-registration-form',
            form: 'websiteUserRegistrationForm',
            isRejected: false,
        };
    },
    computed: {
        hasApproverSignature() {
            return !!_.get(this.$store.state.secure_sign_forms, `${this.form}.approverData.approverSignature`);
        },
    },
    watch: {
        isRejected() {
            this.comment = '';
        },
    },
    created() {
        this.getFormData();
    },
    methods: {
        validateComment(event, confirmActions) {
            this.isRejected = true;
            const isValid = this.$refs.form.validate();

            if (isValid && this.$store.state.secure_sign_forms[this.form].approverData.comment) {
                // If validation passes and we have a comment, trigger the confirm dialog
                confirmActions.click(event);
            } else {
                event.preventDefault();
                event.stopPropagation();
            }
        },
    },
};
</script>

<style scoped>
.previewBtn {
    width: 220px !important;
}

.approve-divider {
    border-width: thin;
}

.bottom-border-1 {
    border-bottom: 1px solid #ccc;
}

.border-approver-details {
    border: 1px solid #ccc;
    border-radius: 9px;
    padding: 5px 16px;
}

.text-grey {
    color: #625f5f;
}

.custom-approver-block {
    padding: 0 !important;
    margin-left: -15px !important;
}

::v-deep .approver-comment-field .v-input__control .v-input__slot {
    border-radius: 6px !important;
}
</style>
